/**
 * Analytics Dashboard Page
 *
 * Main analytics dashboard with summary cards, charts, and recent executions.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnalyticsSubNav } from '../components/layout/AnalyticsLayout';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { LoadingSkeleton } from '../components/common/LoadingSkeleton';
import {
  useAnalyticsDashboard,
  formatDuration,
  formatCost,
  formatPercentage,
  formatNumber,
} from '../hooks/useAnalytics';
import { TimeRange, RecentExecution } from '../types/analytics';

// Colors for pie chart
const CHART_COLORS = [
  '#3b82f6', // Blue
  '#22c55e', // Green
  '#f59e0b', // Amber
  '#ef4444', // Red
  '#8b5cf6', // Purple
  '#06b6d4', // Cyan
  '#ec4899', // Pink
  '#84cc16', // Lime
];

/**
 * Time Range Selector Component
 */
interface TimeRangeSelectorProps {
  value: TimeRange;
  onChange: (range: TimeRange) => void;
}

function TimeRangeSelector({ value, onChange }: TimeRangeSelectorProps) {
  const options: { value: TimeRange; label: string }[] = [
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
  ];

  return (
    <div className="flex items-center gap-2">
      {options.map((option) => (
        <Button
          key={option.value}
          variant={value === option.value ? 'default' : 'outline'}
          size="sm"
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
}

/**
 * Summary Card Component
 */
interface SummaryCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  isLoading?: boolean;
}

function SummaryCard({
  title,
  value,
  subtitle,
  icon,
  trend,
  isLoading,
}: SummaryCardProps) {
  return (
    <Card>
      <CardContent className="pt-6">
        {isLoading ? (
          <>
            <LoadingSkeleton className="h-4 w-24 mb-2" />
            <LoadingSkeleton className="h-8 w-32" />
          </>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">
                {title}
              </span>
              <span className="text-2xl">{icon}</span>
            </div>
            <div className="text-2xl font-bold">{value}</div>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
            {trend && (
              <div
                className={`flex items-center gap-1 mt-2 text-sm ${
                  trend.isPositive ? 'text-green-600' : 'text-red-600'
                }`}
              >
                <span>{trend.isPositive ? '↑' : '↓'}</span>
                <span>{Math.abs(trend.value).toFixed(1)}%</span>
                <span className="text-muted-foreground">vs previous</span>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Execution Status Badge
 */
function ExecutionStatusBadge({ status }: { status: RecentExecution['status'] }) {
  const styles = {
    PENDING: 'bg-gray-100 text-gray-700',
    RUNNING: 'bg-blue-100 text-blue-700',
    COMPLETED: 'bg-green-100 text-green-700',
    FAILED: 'bg-red-100 text-red-700',
    CANCELLED: 'bg-yellow-100 text-yellow-700',
  };

  return (
    <span className={`px-2 py-1 text-xs rounded-full font-medium ${styles[status]}`}>
      {status}
    </span>
  );
}

/**
 * Recent Executions Table
 */
interface RecentExecutionsTableProps {
  executions: RecentExecution[];
  isLoading: boolean;
}

function RecentExecutionsTable({ executions, isLoading }: RecentExecutionsTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <LoadingSkeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (executions.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No recent executions
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b text-left text-sm text-muted-foreground">
            <th className="pb-3 font-medium">Agent</th>
            <th className="pb-3 font-medium">Status</th>
            <th className="pb-3 font-medium">Duration</th>
            <th className="pb-3 font-medium">Cost</th>
            <th className="pb-3 font-medium">Started</th>
          </tr>
        </thead>
        <tbody>
          {executions.map((execution) => (
            <tr key={execution.id} className="border-b last:border-0">
              <td className="py-3">
                <Link
                  to={`/executions/${execution.id}`}
                  className="font-medium hover:text-primary"
                >
                  {execution.agentName}
                </Link>
              </td>
              <td className="py-3">
                <ExecutionStatusBadge status={execution.status} />
              </td>
              <td className="py-3 text-sm">
                {execution.duration ? formatDuration(execution.duration) : '-'}
              </td>
              <td className="py-3 text-sm">
                {execution.cost !== null ? formatCost(execution.cost) : '-'}
              </td>
              <td className="py-3 text-sm text-muted-foreground">
                {new Date(execution.startedAt).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Analytics Dashboard Page Component
 */
export default function AnalyticsDashboard() {
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');

  const { data, isLoading, error } = useAnalyticsDashboard({ timeRange });

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <div className="max-w-7xl mx-auto py-8">
          <Card>
            <CardContent className="py-12 text-center">
              <div className="text-6xl mb-4">❌</div>
              <CardTitle className="mb-2">Error Loading Analytics</CardTitle>
              <CardDescription>
                {error instanceof Error ? error.message : 'An error occurred'}
              </CardDescription>
              <Button
                className="mt-4"
                variant="outline"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const summary = data?.summary;
  const executionTrend = data?.executionTrend;
  const costByAgent = data?.costByAgent || [];
  const recentExecutions = data?.recentExecutions || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-7xl mx-auto py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold">Analytics Dashboard</h1>
            <p className="text-muted-foreground">
              Monitor your agent performance and costs
            </p>
          </div>
          <div className="flex items-center gap-4">
            <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
            <AnalyticsSubNav />
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <SummaryCard
            title="Total Runs"
            value={summary ? formatNumber(summary.executionMetrics.totalRuns) : '-'}
            icon="🚀"
            trend={
              executionTrend
                ? {
                    value: executionTrend.changePercentage,
                    isPositive: executionTrend.changePercentage >= 0,
                  }
                : undefined
            }
            isLoading={isLoading}
          />
          <SummaryCard
            title="Success Rate"
            value={
              summary
                ? formatPercentage(summary.executionMetrics.successRate)
                : '-'
            }
            subtitle={
              summary
                ? `${summary.executionMetrics.successfulRuns} successful`
                : undefined
            }
            icon="✅"
            isLoading={isLoading}
          />
          <SummaryCard
            title="Avg Duration"
            value={
              summary
                ? formatDuration(summary.executionMetrics.averageDuration)
                : '-'
            }
            subtitle={
              summary
                ? `Min: ${formatDuration(summary.executionMetrics.minDuration)}`
                : undefined
            }
            icon="⏱️"
            isLoading={isLoading}
          />
          <SummaryCard
            title="Total Cost"
            value={summary ? formatCost(summary.totalCost) : '-'}
            subtitle={summary ? `${summary.activeAgents} active agents` : undefined}
            icon="💰"
            isLoading={isLoading}
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Executions Over Time Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Executions Over Time</CardTitle>
              <CardDescription>Daily execution counts</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <LoadingSkeleton className="h-64 w-full" />
              ) : executionTrend?.data && executionTrend.data.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={executionTrend.data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) =>
                        new Date(value).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })
                      }
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      labelFormatter={(value) =>
                        new Date(value).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                        })
                      }
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="successful"
                      name="Successful"
                      stroke="#22c55e"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="failed"
                      name="Failed"
                      stroke="#ef4444"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="total"
                      name="Total"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No execution data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cost by Agent Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Cost by Agent</CardTitle>
              <CardDescription>Cost distribution across agents</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <LoadingSkeleton className="h-64 w-full" />
              ) : costByAgent.length > 0 ? (
                <div className="flex items-center">
                  <ResponsiveContainer width="60%" height={280}>
                    <PieChart>
                      <Pie
                        data={costByAgent}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="totalCost"
                        nameKey="agentName"
                      >
                        {costByAgent.map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={CHART_COLORS[index % CHART_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value: number) => formatCost(value)}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="w-40 space-y-2">
                    {costByAgent.slice(0, 5).map((agent, index) => (
                      <div key={agent.agentId} className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{
                            backgroundColor:
                              CHART_COLORS[index % CHART_COLORS.length],
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">
                            {agent.agentName}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatPercentage(agent.percentage)}
                          </p>
                        </div>
                      </div>
                    ))}
                    {costByAgent.length > 5 && (
                      <p className="text-xs text-muted-foreground">
                        +{costByAgent.length - 5} more
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-muted-foreground">
                  No cost data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Executions Table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Recent Executions</CardTitle>
              <CardDescription>Latest agent execution runs</CardDescription>
            </div>
            <Button variant="outline" size="sm" asChild>
              <Link to="/executions">View All</Link>
            </Button>
          </CardHeader>
          <CardContent>
            <RecentExecutionsTable
              executions={recentExecutions}
              isLoading={isLoading}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
